import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import TableActions from "./table-actions";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddNewBtn from "./add-btn";
import { useTranslation } from "react-i18next";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

export type ITreeNode = {
  id: string | number;
  code: string;
  name: string;
  system_name: string;
  system_key: string;
  node_type?: string;
  total?: number;
  has_folder_icon: boolean;
  is_deletable: boolean;
  is_editable: boolean;
  children?: ITreeNode[];
  can_add: boolean;
  is_locked: boolean;
  description?: string;
  chart_account_placement_id?: string | number;
};

type Props = {
  data: ITreeNode[];
  isLoading: boolean;
  refetch: Function;
};

const TreeTable: React.FC<Props> = ({ data, isLoading, refetch }) => {
  const { t: generalT } = useTranslation("general");

  const [expanded, setExpanded] = useState<Record<string | number, boolean>>(
    {},
  );

  const toggle = (id: string | number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderRows = (nodes: ITreeNode[], depth = 0): React.ReactNode[] => {
    return nodes.flatMap((node) => {
      const isOpen = expanded[node.id];
      const hasChildren = node.children && node.children?.length > 0;

      return [
        <TableRow
          key={`${depth}_${node.id}_${node.chart_account_placement_id ?? ""}`}
          hover
          sx={{
            bgcolor:
              node.node_type === "category" ? "primary.light" : "transparent",
          }}
        >
          <TableCell width={80}>
            {node.is_locked ? <LockOutlinedIcon fontSize="small" /> : null}
          </TableCell>
          <TableCell width={80}>{node.code}</TableCell>

          {/* only this column has hierarchy */}
          <TableCell size="small">
            <Box display="flex" alignItems="center" pl={depth * 3}>
              {hasChildren && (
                <IconButton size="small" onClick={() => toggle(node.id)}>
                  {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              )}
              {!hasChildren && <Box width={32} />}
              <Stack direction={"row"} gap={"6px"} alignItems={"center"}>
                {node.has_folder_icon && (
                  <FolderOutlinedIcon fontSize="small" />
                )}
                {node.name}
                {node.node_type !== "category" && !!node.description && (
                  <Tooltip title={node.description}>
                    <InfoOutlinedIcon color="primary" fontSize="small" />
                  </Tooltip>
                )}
                {(node.can_add ||
                  node.system_key === "owner_current_account") &&
                  depth <= 4 && <AddNewBtn row={node} refetch={refetch} />}
              </Stack>
            </Box>
          </TableCell>

          {/* these columns stay aligned */}
          <TableCell>{node.system_name}</TableCell>
          <TableCell>{node.node_type}</TableCell>
          <TableCell>
            <TableActions row={node} refetch={refetch} />
          </TableCell>
          <TableCell align="center">
            <SmartToyOutlinedIcon fontSize="small" color="primary" />
          </TableCell>
          <TableCell>{node.total?.toFixed(2) ?? "-"}</TableCell>
        </TableRow>,

        ...(hasChildren && isOpen ? renderRows(node.children!, depth + 1) : []),
      ];
    });
  };

  const tableHeaders = [
    "code",
    "accountName",
    "systemAccountName",
    "accountType",
    "actions",
    "Auto-Pilot",
    "YTD",
  ];

  return (
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: "primary.main" }}>
          <TableCell> </TableCell>
          {tableHeaders.map((item) => (
            <TableCell key={item}>
              <Typography color={"#fff"} lineHeight={"18px"} fontWeight={500}>
                {generalT(item)}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {isLoading ? (
        <TableLoadingSkeleton rowsLength={5} cellsLength={8} />
      ) : (
        <TableBody>{renderRows(data)}</TableBody>
      )}
    </Table>
  );
};

export default TreeTable;
